<!--
  MANAGER ORDER DETAILS - VUE EXHAUSTIVE SUPPORT CLIENT
  ======================================================

  Affiche TOUTES les données disponibles sur un shipment (UnifiedShipment)
  - Design Befret épuré (#22A922 green)
  - Style harmonisé avec history.component.html
  - Angular Material components (mat-card, mat-expansion-panel, mat-chip)
  - Sections collapsibles avec expand/collapse
  - Print-friendly layout
  - Mode édition inline (admin only)
-->

<main class="manager-order-details-container">

  <!-- ========================================
       1. HEADER (always visible)
       ======================================== -->
  <div class="details-header">
    <div class="container py-4">
      <div class="row align-items-center">
        <div class="col-lg-8">
          <!-- Back Button -->
          <button mat-icon-button (click)="goBack()" class="back-button">
            <mat-icon>arrow_back</mat-icon>
          </button>

          <!-- Page Title -->
          <h1 class="page-title">
            <mat-icon>receipt_long</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.TITLE" | productionTranslate }}
            <span class="tracking-number-badge" *ngIf="shipment">{{ shipment.trackingNumber }}</span>
          </h1>
        </div>

        <!-- Action Buttons -->
        <div class="col-lg-4 text-end">
          <div class="action-buttons" *ngIf="!isLoading && shipment">
            <!-- Edit Mode Toggle (Admin Only) -->
            <button
              mat-raised-button
              (click)="toggleEditMode()"
              class="header-action-button"
              *ngIf="isAdmin"
              [disabled]="isSaving">
              <mat-icon>{{ isEditMode ? 'cancel' : 'edit' }}</mat-icon>
              {{ (isEditMode ? "COMMON.ACTIONS.CANCEL" : "COMMON.ACTIONS.EDIT") | productionTranslate }}
            </button>

            <!-- Heavy Parcel Processing Button (Admin Only, Heavy Category Only) -->
            <button
              mat-raised-button
              (click)="goToHeavyProcessing()"
              *ngIf="isAdmin && shipment.category === 'heavy'"
              [disabled]="isSaving"
              class="header-action-button">
              <mat-icon>settings</mat-icon>
              {{ 'ORDER_DETAILS_MANAGER.ACTIONS.PROCESS_HEAVY' | productionTranslate }}
            </button>

            <!-- Print Button -->
            <button mat-icon-button (click)="printPage()" class="action-icon-btn" matTooltip="{{ 'COMMON.ACTIONS.PRINT' | productionTranslate }}">
              <mat-icon>print</mat-icon>
            </button>

            <!-- Export JSON Button -->
            <button mat-icon-button (click)="exportJSON()" class="action-icon-btn" matTooltip="{{ 'COMMON.ACTIONS.EXPORT_JSON' | productionTranslate }}">
              <mat-icon>download</mat-icon>
            </button>

            <!-- Delete Button (Admin Only, not archived) -->
            <button
              mat-icon-button
              (click)="deleteShipment()"
              class="action-icon-btn delete-btn"
              *ngIf="isAdmin && !isEditMode && !shipment.metadata?.flags?.archived"
              matTooltip="{{ 'COMMON.ACTIONS.DELETE' | productionTranslate }}">
              <mat-icon>delete</mat-icon>
            </button>

            <!-- Restore Button (Admin Only, archived) -->
            <button
              mat-icon-button
              (click)="restoreShipment()"
              class="action-icon-btn restore-icon-btn"
              *ngIf="isAdmin && !isEditMode && shipment.metadata?.flags?.archived"
              matTooltip="Restaurer ce colis">
              <mat-icon>restore_from_trash</mat-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- ========================================
       ARCHIVED BANNER
       ======================================== -->
  <div class="archived-banner" *ngIf="shipment && shipment.metadata?.flags?.archived">
    <div class="container">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center gap-2">
          <mat-icon>delete_outline</mat-icon>
          <span>{{ 'ORDER_DETAILS_MANAGER.DELETED_BANNER' | productionTranslate }}</span>
          <span *ngIf="$any(shipment.metadata?.flags)?.deletedAt" class="archived-date">
            le {{ $any(shipment.metadata.flags).deletedAt | date:'dd/MM/yyyy HH:mm' }}
          </span>
        </div>
        <button mat-raised-button (click)="restoreShipment()" *ngIf="isAdmin" class="restore-btn">
          <mat-icon>restore_from_trash</mat-icon>
          Restaurer
        </button>
      </div>
    </div>
  </div>

  <!-- ========================================
       LOADING STATE
       ======================================== -->
  <section class="pt40 pb40" *ngIf="isLoading">
    <div class="container">
      <mat-card class="loading-card">
        <mat-card-content>
          <div class="loading-container">
            <mat-spinner diameter="50"></mat-spinner>
            <p>{{ "COMMON.LOADING" | productionTranslate }}</p>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </section>

  <!-- ========================================
       ERROR STATE
       ======================================== -->
  <section class="pt40 pb40" *ngIf="!isLoading && error">
    <div class="container">
      <mat-card class="error-card">
        <mat-card-content>
          <div class="error-container">
            <mat-icon class="error-icon">error_outline</mat-icon>
            <h3>{{ "ORDER_DETAILS_MANAGER.ERROR.TITLE" | productionTranslate }}</h3>
            <p>{{ error | productionTranslate }}</p>
            <button mat-raised-button color="primary" (click)="goBack()" class="befret-button">
              <mat-icon>arrow_back</mat-icon>
              {{ "COMMON.ACTIONS.BACK" | productionTranslate }}
            </button>
          </div>
        </mat-card-content>
      </mat-card>
    </div>
  </section>

  <!-- ========================================
       MAIN CONTENT (when shipment loaded)
       ======================================== -->
  <section class="pt40 pb40" *ngIf="!isLoading && !error && shipment">
    <div class="container">

      <!-- Save/Cancel Buttons (Edit Mode - Top) -->
      <div class="edit-mode-toolbar" *ngIf="isEditMode">
        <div class="toolbar-content">
          <mat-icon>info_outline</mat-icon>
          <span>{{ "ORDER_DETAILS_MANAGER.EDIT_MODE.INFO" | productionTranslate }}</span>
          <div class="toolbar-actions">
            <button mat-raised-button (click)="cancelEdit()" class="befret-button-outline" [disabled]="isSaving">
              <mat-icon>cancel</mat-icon>
              {{ "COMMON.ACTIONS.CANCEL" | productionTranslate }}
            </button>
            <button mat-raised-button (click)="saveChanges()" class="befret-button" [disabled]="isSaving || !editForm.valid">
              <mat-icon>save</mat-icon>
              {{ "COMMON.ACTIONS.SAVE" | productionTranslate }}
            </button>
          </div>
        </div>
      </div>

      <!-- ========================================
           2. QUICK ACTIONS CARD (Manager Only)
           ======================================== -->
      <mat-card class="detail-card quick-actions-card" *ngIf="isAdmin && getAvailableQuickActions().length > 0 && !isEditMode">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>flash_on</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.QUICK_ACTIONS.TITLE" | productionTranslate }}
          </mat-card-title>
        </mat-card-header>

        <mat-card-content>
          <div class="quick-actions-info">
            <mat-icon>info</mat-icon>
            <span>{{ "ORDER_DETAILS_MANAGER.QUICK_ACTIONS.INFO" | productionTranslate }}</span>
          </div>

          <div class="quick-actions-buttons">
            <button
              *ngFor="let action of getAvailableQuickActions()"
              mat-raised-button
              (click)="quickAction(action.status, action.locationHint)"
              [disabled]="isSaving"
              [ngClass]="'quick-action-btn-' + action.color"
              class="quick-action-btn">
              <mat-icon>{{ action.icon }}</mat-icon>
              {{ action.label }}
            </button>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- ========================================
           2a-bis. INCIDENT ACTIONS CARD (Manager Only)
           Boutons pour signaler un incident (perdu, endommagé, saisi, dangereux...)
           Séparé visuellement pour éviter les clics accidentels
           ======================================== -->
      <mat-card class="detail-card incident-actions-card" *ngIf="isAdmin && getIncidentActions().length > 0 && !isEditMode">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>warning</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.INCIDENT_ACTIONS.TITLE" | productionTranslate }}
          </mat-card-title>
        </mat-card-header>

        <mat-card-content>
          <div class="quick-actions-info incident-warning">
            <mat-icon>error_outline</mat-icon>
            <span>{{ "ORDER_DETAILS_MANAGER.INCIDENT_ACTIONS.INFO" | productionTranslate }}</span>
          </div>

          <div class="quick-actions-buttons incident-buttons">
            <button
              *ngFor="let action of getIncidentActions()"
              mat-raised-button
              (click)="quickAction(action.status, action.locationHint)"
              [disabled]="isSaving"
              [ngClass]="'incident-btn-' + action.color"
              class="quick-action-btn incident-btn">
              <mat-icon>{{ action.icon }}</mat-icon>
              {{ action.label }}
            </button>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- ========================================
           2b. COLLECTE MANUELLE CARD (Manager Only - Pre-Warehouse Status)
           Permet de bypass le flux DPD quand le colis est collecté directement
           TEMPORAIREMENT DÉSACTIVÉ - À finaliser
           ========================================
      <mat-card class="detail-card quick-actions-card" *ngIf="isAdmin && isPreWarehouseStatus() && !isEditMode">
        <mat-card-header>
          <mat-card-title>
            <mat-icon>local_shipping</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.MANUAL_COLLECTION.TITLE" | productionTranslate }}
          </mat-card-title>
        </mat-card-header>

        <mat-card-content>
          <div class="quick-actions-info">
            <mat-icon>info</mat-icon>
            <span>{{ "ORDER_DETAILS_MANAGER.MANUAL_COLLECTION.INFO" | productionTranslate }}</span>
          </div>

          <div class="manual-collection-checkbox">
            <mat-checkbox
              [(ngModel)]="isManualCollection"
              color="primary"
              class="manual-checkbox">
              {{ "ORDER_DETAILS_MANAGER.MANUAL_COLLECTION.CHECKBOX_LABEL" | productionTranslate }}
            </mat-checkbox>
          </div>

          <div class="quick-actions-buttons" style="margin-top: 1rem;">
            <button
              mat-raised-button
              (click)="quickActionManualCollection()"
              [disabled]="isSaving || !isManualCollection"
              class="quick-action-btn quick-action-btn-primary">
              <mat-icon>warehouse</mat-icon>
              {{ "ORDER_DETAILS_MANAGER.MANUAL_COLLECTION.BUTTON_LABEL" | productionTranslate }}
            </button>
          </div>
        </mat-card-content>
      </mat-card>
      -->

      <!-- ========================================
           3. BASIC INFO CARD
           ======================================== -->
      <mat-card class="detail-card">
        <mat-card-header (click)="toggleSection('basicInfo')" class="clickable-header">
          <mat-card-title>
            <mat-icon>info</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.SECTIONS.BASIC_INFO" | productionTranslate }}
          </mat-card-title>
          <button mat-icon-button class="expand-button">
            <mat-icon>{{ expandedSections['basicInfo'] ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </mat-card-header>

        <mat-card-content *ngIf="expandedSections['basicInfo']">
          <div class="detail-grid">
            <!-- Tracking Number -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.TRACKING_NUMBER" | productionTranslate }}</label>
              <div class="value-with-copy">
                <span class="value-primary">{{ shipment.trackingNumber }}</span>
                <button mat-icon-button (click)="copyToClipboard(shipment.trackingNumber, 'Tracking Number')" class="copy-btn">
                  <mat-icon>content_copy</mat-icon>
                </button>
              </div>
            </div>

            <!-- Status Chip (Technical - 99 statuts détaillés) -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.STATUS" | productionTranslate }} (Technique)</label>
              <mat-chip [ngClass]="'status-chip-' + shipment.status.current" class="status-chip">
                {{ formatter.getStatusLabel(shipment.status.current, shipment.category, shipment.webhookStatus) }}
              </mat-chip>
              <div class="status-code-hint" style="font-size: 11px; color: #666; margin-top: 4px;">
                Code: {{ shipment.status.current }}
              </div>
            </div>

            <!-- Status Client Simplifié (Timeline Step - ce que voit le client) -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.STATUS" | productionTranslate }} (Client)</label>
              <mat-chip class="status-chip status-chip-client">
                {{ "PARCEL_DETAILS.TIMELINE.STEPS." + formatter.getTimelineStep(shipment.status.current, shipment.category, shipment.webhookStatus).toUpperCase() + ".TITLE" | productionTranslate }}
              </mat-chip>
            </div>

            <!-- Phase Chip -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.PHASE" | productionTranslate }}</label>
              <mat-chip [ngClass]="'phase-chip-' + (shipment.phase?.current || 'unknown')" class="phase-chip">
                {{ shipment.phase?.current ? (("ORDER_DETAILS_MANAGER.VALUES.PHASES." + shipment.phase.current) | productionTranslate) : 'N/A' }}
              </mat-chip>
            </div>

            <!-- Category -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.CATEGORY" | productionTranslate }}</label>
              <span class="value">{{ shipment.category | uppercase }}</span>
            </div>

            <!-- Type -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.TYPE" | productionTranslate }}</label>
              <span class="value">{{ shipment.type }}</span>
            </div>

            <!-- Business Model -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.BUSINESS_MODEL" | productionTranslate }}</label>
              <span class="value">{{ ("ORDER_DETAILS_MANAGER.VALUES.BUSINESS_MODELS." + shipment.businessModel) | productionTranslate }}</span>
            </div>

            <!-- Created Date -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.CREATED_DATE" | productionTranslate }}</label>
              <span class="value">{{ formatter.formatDate(shipment.timestamps?.createdAt) }}</span>
            </div>

            <!-- Updated Date -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.UPDATED_DATE" | productionTranslate }}</label>
              <span class="value">{{ formatter.formatDate(shipment.timestamps?.updatedAt) }}</span>
            </div>

            <!-- User ID -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.USER_ID" | productionTranslate }}</label>
              <div class="value-with-copy">
                <span class="value">{{ shipment.userId }}</span>
                <button mat-icon-button (click)="copyToClipboard(shipment.userId, 'User ID')" class="copy-btn">
                  <mat-icon>content_copy</mat-icon>
                </button>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- ========================================
           3. CUSTOMER INFO CARD
           ======================================== -->
      <mat-card class="detail-card">
        <mat-card-header (click)="toggleSection('customerInfo')" class="clickable-header">
          <mat-card-title>
            <mat-icon>people</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.SECTIONS.CUSTOMER_INFO" | productionTranslate }}
          </mat-card-title>
          <button mat-icon-button class="expand-button">
            <mat-icon>{{ expandedSections['customerInfo'] ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </mat-card-header>

        <mat-card-content *ngIf="expandedSections['customerInfo']">
          <div class="customer-sections">

            <!-- SENDER Section -->
            <div class="customer-section">
              <h3 class="section-subtitle">
                <mat-icon>person_outline</mat-icon>
                {{ "ORDER_DETAILS_MANAGER.LABELS.SENDER" | productionTranslate }}
              </h3>
              <div class="detail-grid">
                <!-- Sender Name -->
                <div class="detail-item">
                  <label>{{ "ORDER_DETAILS_MANAGER.LABELS.NAME" | productionTranslate }}</label>
                  <span class="value" *ngIf="!isEditMode">{{ shipment.customerInfo?.sender?.name }}</span>
                  <input
                    *ngIf="isEditMode"
                    type="text"
                    class="form-control"
                    [formControl]="$any(editForm.get('senderName'))">
                </div>

                <!-- Sender Email -->
                <div class="detail-item">
                  <label>{{ "ORDER_DETAILS_MANAGER.LABELS.EMAIL" | productionTranslate }}</label>
                  <div class="value-with-copy" *ngIf="!isEditMode">
                    <span class="value">{{ shipment.customerInfo?.sender?.email || 'N/A' }}</span>
                    <button
                      mat-icon-button
                      (click)="copyToClipboard(shipment.customerInfo?.sender?.email || '', 'Sender Email')"
                      class="copy-btn"
                      *ngIf="shipment.customerInfo?.sender?.email">
                      <mat-icon>content_copy</mat-icon>
                    </button>
                  </div>
                  <input
                    *ngIf="isEditMode"
                    type="email"
                    class="form-control"
                    [formControl]="$any(editForm.get('senderEmail'))">
                </div>

                <!-- Sender Phone -->
                <div class="detail-item">
                  <label>{{ "ORDER_DETAILS_MANAGER.LABELS.PHONE" | productionTranslate }}</label>
                  <div class="value-with-copy" *ngIf="!isEditMode">
                    <span class="value">{{ formatter.formatPhone(shipment.customerInfo?.sender?.phone) }}</span>
                    <button
                      mat-icon-button
                      (click)="copyToClipboard(formatter.formatPhone(shipment.customerInfo?.sender?.phone), 'Sender Phone')"
                      class="copy-btn">
                      <mat-icon>content_copy</mat-icon>
                    </button>
                  </div>
                  <input
                    *ngIf="isEditMode"
                    type="text"
                    class="form-control"
                    [formControl]="$any(editForm.get('senderPhone'))">
                </div>

                <!-- Sender Full Address -->
                <div class="detail-item full-width" *ngIf="shipment.customerInfo?.sender?.address">
                  <label>{{ "ORDER_DETAILS_MANAGER.LABELS.ADDRESS" | productionTranslate }}</label>
                  <div *ngIf="!isEditMode" class="address-block">
                    <div>{{ shipment.customerInfo?.sender?.address?.street }} {{ shipment.customerInfo?.sender?.address?.houseNumber }}</div>
                    <div *ngIf="shipment.customerInfo?.sender?.address?.addressComplement" class="text-muted"><small>{{ shipment.customerInfo?.sender?.address?.addressComplement }}</small></div>
                    <div>{{ shipment.customerInfo?.sender?.address?.zipCode }} {{ shipment.customerInfo?.sender?.address?.city }}</div>
                    <div>{{ shipment.customerInfo?.sender?.address?.country }}</div>
                  </div>
                  <div *ngIf="isEditMode" class="address-edit-grid">
                    <input type="text" class="form-control" placeholder="Street" [formControl]="$any(editForm.get('senderStreet'))">
                    <input type="text" class="form-control" placeholder="House Number" [formControl]="$any(editForm.get('senderHouseNumber'))">
                    <input type="text" class="form-control" placeholder="Postal Code" [formControl]="$any(editForm.get('senderPostalCode'))">
                    <input type="text" class="form-control" placeholder="City" [formControl]="$any(editForm.get('senderCity'))">
                    <input type="text" class="form-control" placeholder="Country" [formControl]="$any(editForm.get('senderCountry'))">
                  </div>
                </div>
              </div>
            </div>

            <mat-divider class="section-divider"></mat-divider>

            <!-- RECEIVER Section -->
            <div class="customer-section">
              <h3 class="section-subtitle">
                <mat-icon>person</mat-icon>
                {{ "ORDER_DETAILS_MANAGER.LABELS.RECEIVER" | productionTranslate }}
              </h3>
              <div class="detail-grid">
                <!-- Receiver Name -->
                <div class="detail-item">
                  <label>{{ "ORDER_DETAILS_MANAGER.LABELS.NAME" | productionTranslate }}</label>
                  <span class="value" *ngIf="!isEditMode">{{ shipment.customerInfo?.receiver?.name }}</span>
                  <input
                    *ngIf="isEditMode"
                    type="text"
                    class="form-control"
                    [formControl]="$any(editForm.get('receiverName'))">
                </div>

                <!-- Receiver Email -->
                <div class="detail-item">
                  <label>{{ "ORDER_DETAILS_MANAGER.LABELS.EMAIL" | productionTranslate }}</label>
                  <div class="value-with-copy" *ngIf="!isEditMode">
                    <span class="value">{{ shipment.customerInfo?.receiver?.email || 'N/A' }}</span>
                    <button
                      mat-icon-button
                      (click)="copyToClipboard(shipment.customerInfo?.receiver?.email || '', 'Receiver Email')"
                      class="copy-btn"
                      *ngIf="shipment.customerInfo?.receiver?.email">
                      <mat-icon>content_copy</mat-icon>
                    </button>
                  </div>
                  <input
                    *ngIf="isEditMode"
                    type="email"
                    class="form-control"
                    [formControl]="$any(editForm.get('receiverEmail'))">
                </div>

                <!-- Receiver Phone -->
                <div class="detail-item">
                  <label>{{ "ORDER_DETAILS_MANAGER.LABELS.PHONE" | productionTranslate }}</label>
                  <div class="value-with-copy" *ngIf="!isEditMode">
                    <span class="value">{{ formatter.formatPhone(shipment.customerInfo?.receiver?.phone) }}</span>
                    <button
                      mat-icon-button
                      (click)="copyToClipboard(formatter.formatPhone(shipment.customerInfo?.receiver?.phone), 'Receiver Phone')"
                      class="copy-btn">
                      <mat-icon>content_copy</mat-icon>
                    </button>
                  </div>
                  <input
                    *ngIf="isEditMode"
                    type="text"
                    class="form-control"
                    [formControl]="$any(editForm.get('receiverPhone'))">
                </div>

                <!-- Receiver Full Address -->
                <div class="detail-item full-width" *ngIf="shipment.customerInfo?.receiver?.address">
                  <label>{{ "ORDER_DETAILS_MANAGER.LABELS.ADDRESS" | productionTranslate }}</label>
                  <div *ngIf="!isEditMode" class="address-block">
                    <div>{{ shipment.customerInfo?.receiver?.address?.street }}</div>
                    <div>{{ shipment.customerInfo?.receiver?.address?.zipCode }} {{ shipment.customerInfo?.receiver?.address?.city }}</div>
                    <div>{{ shipment.customerInfo?.receiver?.address?.country }}</div>
                  </div>
                  <div *ngIf="isEditMode" class="address-edit-grid">
                    <input type="text" class="form-control" placeholder="Street" [formControl]="$any(editForm.get('receiverStreet'))">
                    <input type="text" class="form-control" placeholder="Postal Code" [formControl]="$any(editForm.get('receiverPostalCode'))">
                    <input type="text" class="form-control" placeholder="City" [formControl]="$any(editForm.get('receiverCity'))">
                    <input type="text" class="form-control" placeholder="Country" [formControl]="$any(editForm.get('receiverCountry'))">
                  </div>
                </div>
              </div>
            </div>

            <mat-divider class="section-divider"></mat-divider>

            <!-- PREFERENCES Section -->
            <div class="customer-section">
              <h3 class="section-subtitle">
                <mat-icon>settings</mat-icon>
                {{ "ORDER_DETAILS_MANAGER.LABELS.PREFERENCES" | productionTranslate }}
              </h3>
              <div class="detail-grid">
                <div class="detail-item">
                  <label>{{ "ORDER_DETAILS_MANAGER.LABELS.LANGUAGE" | productionTranslate }}</label>
                  <span class="value">{{ shipment.customerInfo?.preferences?.language | uppercase }}</span>
                </div>
                <div class="detail-item">
                  <label>{{ "ORDER_DETAILS_MANAGER.LABELS.TIMEZONE" | productionTranslate }}</label>
                  <span class="value">{{ shipment.customerInfo?.preferences?.timezone }}</span>
                </div>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- ========================================
           4. PARCEL INFO CARD
           ======================================== -->
      <mat-card class="detail-card">
        <mat-card-header (click)="toggleSection('parcelInfo')" class="clickable-header">
          <mat-card-title>
            <mat-icon>inventory_2</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.SECTIONS.PARCEL_INFO" | productionTranslate }}
          </mat-card-title>
          <button mat-icon-button class="expand-button">
            <mat-icon>{{ expandedSections['parcelInfo'] ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </mat-card-header>

        <mat-card-content *ngIf="expandedSections['parcelInfo']">
          <div class="detail-grid">
            <!-- Weight -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.WEIGHT" | productionTranslate }}</label>
              <span class="value value-highlight" *ngIf="!isEditMode">{{ shipment.parcelInfo?.weight }} kg</span>
              <input
                *ngIf="isEditMode"
                type="number"
                class="form-control"
                [formControl]="$any(editForm.get('weight'))">
            </div>

            <!-- Dimensions -->
            <div class="detail-item" *ngIf="shipment.parcelInfo?.dimensions">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.DIMENSIONS" | productionTranslate }}</label>
              <span class="value" *ngIf="!isEditMode">
                {{ shipment.parcelInfo?.dimensions?.length }} x
                {{ shipment.parcelInfo?.dimensions?.width }} x
                {{ shipment.parcelInfo?.dimensions?.height }} cm
              </span>
              <div *ngIf="isEditMode" class="dimensions-edit">
                <input type="number" class="form-control" placeholder="L" [formControl]="$any(editForm.get('length'))">
                <span>×</span>
                <input type="number" class="form-control" placeholder="W" [formControl]="$any(editForm.get('width'))">
                <span>×</span>
                <input type="number" class="form-control" placeholder="H" [formControl]="$any(editForm.get('height'))">
                <span>cm</span>
              </div>
            </div>

            <!-- Volume (calculated) -->
            <div class="detail-item" *ngIf="shipment.parcelInfo?.dimensions?.volume">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.VOLUME" | productionTranslate }}</label>
              <span class="value">{{ shipment.parcelInfo?.dimensions?.volume }} cm³</span>
            </div>

            <!-- Declared value (customer-declared, not insured) -->
            <div class="detail-item" data-testid="declared-value">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.DECLARED_VALUE" | productionTranslate }}</label>
              <span class="value" *ngIf="declaredValue as declared; else notDeclared">
                {{ declared.amount | number:'1.2-2' }} {{ declared.currency === 'EUR' ? '€' : declared.currency }}
                ({{ "ORDER_DETAILS_MANAGER.VALUES.DECLARED_VALUE_NOTE" | productionTranslate }})
              </span>
              <ng-template #notDeclared>
                <span class="value">{{ "ORDER_DETAILS_MANAGER.VALUES.NOT_DECLARED" | productionTranslate }}</span>
              </ng-template>
            </div>

            <!-- Content Type -->
            <div class="detail-item" *ngIf="shipment.parcelInfo?.contentType">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.CONTENT_TYPE" | productionTranslate }}</label>
              <span class="value">{{ ("ORDER_DETAILS_MANAGER.VALUES.CONTENT_TYPES." + shipment.parcelInfo.contentType) | productionTranslate }}</span>
            </div>

            <!-- Delivery Method -->
            <div class="detail-item" *ngIf="shipment.parcelInfo?.deliveryMethod">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.DELIVERY_METHOD" | productionTranslate }}</label>
              <span class="value">{{ deliveryMethodLabelKey(shipment.parcelInfo.deliveryMethod) | productionTranslate }}</span>
            </div>

            <!-- Description -->
            <div class="detail-item full-width">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.DESCRIPTION" | productionTranslate }}</label>
              <span class="value" *ngIf="!isEditMode">{{ shipment.parcelInfo?.description }}</span>
              <textarea
                *ngIf="isEditMode"
                class="form-control"
                rows="3"
                [formControl]="$any(editForm.get('description'))"></textarea>
            </div>

            <!-- OPTIONS -->
            <div class="detail-item full-width">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.OPTIONS" | productionTranslate }}</label>
              <div class="options-chips">
                <mat-chip class="option-chip" *ngIf="shipment.parcelInfo?.options?.reinforcedPackaging">
                  <mat-icon>security</mat-icon>
                  {{ "ORDER_DETAILS_MANAGER.LABELS.REINFORCED_PACKAGING" | productionTranslate }}
                </mat-chip>
                <mat-chip class="option-chip" *ngIf="shipment.parcelInfo?.options?.fragileHandling">
                  <mat-icon>warning</mat-icon>
                  {{ "ORDER_DETAILS_MANAGER.LABELS.FRAGILE" | productionTranslate }}
                </mat-chip>
                <mat-chip class="option-chip" *ngIf="shipment.parcelInfo?.options?.requiresSignature">
                  <mat-icon>draw</mat-icon>
                  {{ "ORDER_DETAILS_MANAGER.LABELS.SIGNATURE_REQUIRED" | productionTranslate }}
                </mat-chip>
              </div>
            </div>

            <!-- Special Instructions -->
            <div class="detail-item full-width" *ngIf="shipment.parcelInfo?.options?.specialInstructions">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.SPECIAL_INSTRUCTIONS" | productionTranslate }}</label>
              <div class="special-instructions-box">
                {{ shipment.parcelInfo.options.specialInstructions }}
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- ========================================
           5. PRICING DETAILS CARD
           ======================================== -->
      <mat-card class="detail-card" *ngIf="shipment.standardData?.pricing || shipment.heavyData?.quotationData">
        <mat-card-header (click)="toggleSection('pricing')" class="clickable-header">
          <mat-card-title>
            <mat-icon>euro</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.SECTIONS.PRICING" | productionTranslate }}
          </mat-card-title>
          <button mat-icon-button class="expand-button">
            <mat-icon>{{ expandedSections['pricing'] ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </mat-card-header>

        <mat-card-content *ngIf="expandedSections['pricing']">
          <!-- ✅ STANDARD PARCEL PRICING -->
          <div class="pricing-grid" *ngIf="shipment.standardData?.pricing">
            <!-- Base Price -->
            <div class="pricing-row">
              <span class="pricing-label">{{ "ORDER_DETAILS_MANAGER.LABELS.BASE_PRICE" | productionTranslate }}</span>
              <span class="pricing-value">{{ shipment.standardData?.pricing?.basePrice | number:'1.2-2' }} €</span>
            </div>

            <!-- Weight Surcharge -->
            <div class="pricing-row" *ngIf="shipment.standardData?.pricing?.weightSurcharge">
              <span class="pricing-label">{{ "ORDER_DETAILS_MANAGER.LABELS.WEIGHT_SURCHARGE" | productionTranslate }}</span>
              <span class="pricing-value">{{ shipment.standardData?.pricing?.weightSurcharge | number:'1.2-2' }} €</span>
            </div>

            <!-- Service Fee -->
            <div class="pricing-row" *ngIf="shipment.standardData?.pricing?.serviceFee">
              <span class="pricing-label">{{ "ORDER_DETAILS_MANAGER.LABELS.SERVICE_FEE" | productionTranslate }}</span>
              <span class="pricing-value">{{ shipment.standardData?.pricing?.serviceFee | number:'1.2-2' }} €</span>
            </div>

            <!-- Taxes -->
            <div class="pricing-row" *ngIf="shipment.standardData?.pricing?.taxes">
              <span class="pricing-label">{{ "ORDER_DETAILS_MANAGER.LABELS.TAXES" | productionTranslate }}</span>
              <span class="pricing-value">{{ shipment.standardData?.pricing?.taxes | number:'1.2-2' }} €</span>
            </div>

            <mat-divider class="pricing-divider"></mat-divider>

            <!-- TOTAL -->
            <div class="pricing-row pricing-total">
              <span class="pricing-label">{{ "ORDER_DETAILS_MANAGER.LABELS.TOTAL" | productionTranslate }}</span>
              <span class="pricing-value">{{ shipment.standardData?.pricing?.total | number:'1.2-2' }} €</span>
            </div>

            <!-- Currency -->
            <div class="pricing-row">
              <span class="pricing-label">{{ "ORDER_DETAILS_MANAGER.LABELS.CURRENCY" | productionTranslate }}</span>
              <span class="pricing-value">{{ shipment.standardData?.pricing?.currency }}</span>
            </div>
          </div>

          <!-- ✅ HEAVY PARCEL QUOTE PRICING -->
          <div class="pricing-grid" *ngIf="shipment.heavyData?.quotationData">
            <!-- Quote Status -->
            <div class="pricing-row">
              <span class="pricing-label">{{ "ORDER_DETAILS_MANAGER.LABELS.QUOTE_STATUS" | productionTranslate }}</span>
              <mat-chip [ngClass]="'quote-status-' + shipment.heavyData?.quotationData?.status">
                {{ shipment.heavyData?.quotationData?.status || 'PENDING' }}
              </mat-chip>
            </div>

            <!-- Amount -->
            <div class="pricing-row pricing-total" *ngIf="shipment.heavyData?.quotationData?.amount">
              <span class="pricing-label">{{ "ORDER_DETAILS_MANAGER.LABELS.QUOTE_AMOUNT" | productionTranslate }}</span>
              <span class="pricing-value">{{ shipment.heavyData?.quotationData?.amount | number:'1.2-2' }} {{ shipment.heavyData?.quotationData?.currency }}</span>
            </div>

            <!-- Quote Valid Until -->
            <div class="pricing-row" *ngIf="shipment.heavyData?.quotationData?.validUntil">
              <span class="pricing-label">{{ "ORDER_DETAILS_MANAGER.LABELS.VALID_UNTIL" | productionTranslate }}</span>
              <span class="pricing-value">{{ shipment.heavyData?.quotationData?.validUntil }}</span>
            </div>

            <!-- Quotation PDF -->
            <div class="pricing-row" *ngIf="shipment.heavyData?.quotationData?.quotationPdf">
              <span class="pricing-label">{{ "ORDER_DETAILS_MANAGER.LABELS.QUOTATION_PDF" | productionTranslate }}</span>
              <a [href]="shipment.heavyData?.quotationData?.quotationPdf" target="_blank" class="befret-link">
                <mat-icon>download</mat-icon> {{ 'ORDER_DETAILS_MANAGER.ACTIONS.DOWNLOAD' | productionTranslate }}
              </a>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- ========================================
           6. DPD INTEGRATION CARD
           ======================================== -->
      <mat-card class="detail-card" *ngIf="shipment.standardData">
        <mat-card-header (click)="toggleSection('dpdIntegration')" class="clickable-header">
          <mat-card-title>
            <mat-icon>local_shipping</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.SECTIONS.DPD_INTEGRATION" | productionTranslate }}
          </mat-card-title>
          <button mat-icon-button class="expand-button">
            <mat-icon>{{ expandedSections['dpdIntegration'] ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </mat-card-header>

        <mat-card-content *ngIf="expandedSections['dpdIntegration']">
          <div class="detail-grid">
            <!-- DPD Tracking Number -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.DPD_TRACKING_NUMBER" | productionTranslate }}</label>
              <div class="value-with-copy">
                <span class="value">{{ shipment.standardData.dpdTrackingNumber || 'N/A' }}</span>
                <button
                  mat-icon-button
                  (click)="copyToClipboard(shipment.standardData.dpdTrackingNumber, 'DPD Tracking Number')"
                  class="copy-btn"
                  *ngIf="shipment.standardData.dpdTrackingNumber">
                  <mat-icon>content_copy</mat-icon>
                </button>
              </div>
            </div>

            <!-- DPD Shipment ID -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.DPD_SHIPMENT_ID" | productionTranslate }}</label>
              <div class="value-with-copy">
                <span class="value">{{ shipment.standardData.dpdShipmentId || 'N/A' }}</span>
                <button
                  mat-icon-button
                  (click)="copyToClipboard(shipment.standardData.dpdShipmentId, 'DPD Shipment ID')"
                  class="copy-btn"
                  *ngIf="shipment.standardData.dpdShipmentId">
                  <mat-icon>content_copy</mat-icon>
                </button>
              </div>
            </div>

            <!-- Label URL -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.LABEL_URL" | productionTranslate }}</label>
              <button
                mat-raised-button
                (click)="downloadLabel()"
                class="befret-button-outline"
                *ngIf="canDownloadLabel()">
                <mat-icon>download</mat-icon>
                {{ "COMMON.ACTIONS.DOWNLOAD_LABEL" | productionTranslate }}
              </button>
              <span class="value" *ngIf="!canDownloadLabel()">N/A</span>
            </div>

            <!-- Service Type -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.SERVICE_TYPE" | productionTranslate }}</label>
              <span class="value">{{ ("ORDER_DETAILS_MANAGER.VALUES.SERVICE_TYPES." + (shipment.standardData.serviceType | uppercase)) | productionTranslate }}</span>
            </div>

            <!-- Pickup Point (if applicable) -->
            <div class="detail-item full-width" *ngIf="shipment.standardData.pickupPoint">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.PICKUP_POINT" | productionTranslate }}</label>
              <div class="pickup-point-box">
                <div class="pickup-point-name">{{ shipment.standardData?.pickupPoint?.name }}</div>
                <div class="pickup-point-address">
                  {{ shipment.standardData?.pickupPoint?.address?.street }}<br>
                  {{ shipment.standardData?.pickupPoint?.address?.zipCode }} {{ shipment.standardData?.pickupPoint?.address?.city }}
                </div>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- ========================================
           7. PAYMENT FLOW CARD
           ======================================== -->
      <mat-card class="detail-card" *ngIf="shipment.paymentFlow || shipment.standardData">
        <mat-card-header (click)="toggleSection('paymentFlow')" class="clickable-header">
          <mat-card-title>
            <mat-icon>payment</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.SECTIONS.PAYMENT_FLOW" | productionTranslate }}
          </mat-card-title>
          <button mat-icon-button class="expand-button">
            <mat-icon>{{ expandedSections['paymentFlow'] ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </mat-card-header>

        <mat-card-content *ngIf="expandedSections['paymentFlow']">
          <div class="detail-grid">
            <!-- Stripe Session ID -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.STRIPE_SESSION_ID" | productionTranslate }}</label>
              <div class="value-with-copy">
                <span class="value small-text">
                  {{ shipment.paymentFlow?.stripeSessionId || shipment.standardData?.stripeSessionId || 'N/A' }}
                </span>
                <button
                  mat-icon-button
                  (click)="copyToClipboard(shipment.paymentFlow?.stripeSessionId || shipment.standardData?.stripeSessionId || '', 'Stripe Session ID')"
                  class="copy-btn"
                  *ngIf="shipment.paymentFlow?.stripeSessionId || shipment.standardData?.stripeSessionId">
                  <mat-icon>content_copy</mat-icon>
                </button>
              </div>
            </div>

            <!-- Payment Intent ID -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.PAYMENT_INTENT_ID" | productionTranslate }}</label>
              <div class="value-with-copy">
                <span class="value small-text">
                  {{ shipment.paymentFlow?.paymentIntentId || shipment.standardData?.paymentIntentId || 'N/A' }}
                </span>
                <button
                  mat-icon-button
                  (click)="copyToClipboard(shipment.paymentFlow?.paymentIntentId || shipment.standardData?.paymentIntentId || '', 'Payment Intent ID')"
                  class="copy-btn"
                  *ngIf="shipment.paymentFlow?.paymentIntentId || shipment.standardData?.paymentIntentId">
                  <mat-icon>content_copy</mat-icon>
                </button>
              </div>
            </div>

            <!-- Payment Status -->
            <div class="detail-item" *ngIf="shipment.paymentFlow?.paymentStatus">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.PAYMENT_STATUS" | productionTranslate }}</label>
              <mat-chip [ngClass]="'payment-status-' + shipment.paymentFlow?.paymentStatus" class="status-chip">
                {{ shipment.paymentFlow?.paymentStatus | uppercase }}
              </mat-chip>
            </div>

            <!-- Payment Date -->
            <div class="detail-item" *ngIf="shipment.paymentFlow?.paymentDate">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.PAYMENT_DATE" | productionTranslate }}</label>
              <span class="value">{{ formatter.formatDate(shipment.paymentFlow?.paymentDate) }}</span>
            </div>

            <!-- Payment Amount -->
            <div class="detail-item" *ngIf="shipment.paymentFlow?.paymentAmount">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.PAYMENT_AMOUNT" | productionTranslate }}</label>
              <span class="value value-highlight">
                {{ ((shipment.paymentFlow?.paymentAmount || 0) / 100) | number:'1.2-2' }} {{ shipment.paymentFlow?.paymentCurrency }}
              </span>
            </div>

            <!-- Webhook Processed -->
            <div class="detail-item" *ngIf="shipment.paymentFlow">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.WEBHOOK_PROCESSED" | productionTranslate }}</label>
              <mat-icon [ngClass]="shipment.paymentFlow?.webhookProcessed ? 'check-icon' : 'cross-icon'">
                {{ shipment.paymentFlow?.webhookProcessed ? 'check_circle' : 'cancel' }}
              </mat-icon>
            </div>

            <!-- Architecture Version -->
            <div class="detail-item" *ngIf="shipment.paymentFlow?.architecture">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.ARCHITECTURE" | productionTranslate }}</label>
              <span class="value">{{ shipment.paymentFlow?.architecture }}</span>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- ========================================
           8. METADATA CARD
           ======================================== -->
      <mat-card class="detail-card">
        <mat-card-header (click)="toggleSection('metadata')" class="clickable-header">
          <mat-card-title>
            <mat-icon>settings</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.SECTIONS.METADATA" | productionTranslate }}
          </mat-card-title>
          <button mat-icon-button class="expand-button">
            <mat-icon>{{ expandedSections['metadata'] ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </mat-card-header>

        <mat-card-content *ngIf="expandedSections['metadata']">
          <div class="detail-grid">
            <!-- Version -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.VERSION" | productionTranslate }}</label>
              <span class="value">{{ shipment.metadata?.version }}</span>
            </div>

            <!-- Source -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.SOURCE" | productionTranslate }}</label>
              <span class="value">{{ shipment.metadata?.source }}</span>
            </div>

            <!-- Environment -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.ENVIRONMENT" | productionTranslate }}</label>
              <mat-chip [ngClass]="'env-chip-' + shipment.metadata?.environment" class="env-chip">
                {{ shipment.metadata?.environment | uppercase }}
              </mat-chip>
            </div>

            <!-- Correlation ID -->
            <div class="detail-item" *ngIf="shipment.metadata?.correlationId">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.CORRELATION_ID" | productionTranslate }}</label>
              <span class="value small-text">{{ shipment.metadata.correlationId }}</span>
            </div>

            <!-- Flags -->
            <div class="detail-item full-width" *ngIf="shipment.metadata?.flags">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.FLAGS" | productionTranslate }}</label>
              <div class="flags-chips">
                <mat-chip class="flag-chip" *ngIf="shipment.metadata.flags.testShipment">
                  <mat-icon>science</mat-icon>
                  TEST
                </mat-chip>
                <mat-chip class="flag-chip" *ngIf="shipment.metadata.flags.mockData">
                  <mat-icon>table_view</mat-icon>
                  MOCK
                </mat-chip>
                <mat-chip class="flag-chip" *ngIf="shipment.metadata.flags.archived">
                  <mat-icon>archive</mat-icon>
                  ARCHIVED
                </mat-chip>
                <mat-chip class="flag-chip" *ngIf="shipment.metadata.flags.migrated">
                  <mat-icon>sync_alt</mat-icon>
                  MIGRATED
                </mat-chip>
                <mat-chip class="flag-chip flag-chip-validated" *ngIf="shipment.metadata.flags.validated">
                  <mat-icon>verified</mat-icon>
                  VALIDATED
                </mat-chip>
              </div>
            </div>

            <!-- Tags -->
            <div class="detail-item full-width" *ngIf="shipment.metadata?.tags && shipment.metadata.tags.length > 0">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.TAGS" | productionTranslate }}</label>
              <div class="tags-chips">
                <mat-chip class="tag-chip" *ngFor="let tag of shipment.metadata.tags">
                  {{ tag }}
                </mat-chip>
              </div>
            </div>

            <!-- Migration Info -->
            <div class="detail-item full-width" *ngIf="shipment.metadata?.migrationInfo">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.MIGRATION_INFO" | productionTranslate }}</label>
              <div class="migration-box">
                <div><strong>Migrated From:</strong> {{ shipment.metadata?.migrationInfo?.migratedFrom }}</div>
                <div><strong>Migration Date:</strong> {{ formatter.formatDate(shipment.metadata?.migrationInfo?.migrationDate) }}</div>
                <div><strong>Original ID:</strong> {{ shipment.metadata?.migrationInfo?.originalId }}</div>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- ========================================
           9. STATUS HISTORY CARD
           ======================================== -->
      <mat-card class="detail-card" *ngIf="shipment.statusHistory && shipment.statusHistory.length > 0">
        <mat-card-header (click)="toggleSection('statusHistory')" class="clickable-header">
          <mat-card-title>
            <mat-icon>timeline</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.SECTIONS.STATUS_HISTORY" | productionTranslate }}
          </mat-card-title>
          <button mat-icon-button class="expand-button">
            <mat-icon>{{ expandedSections['statusHistory'] ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </mat-card-header>

        <mat-card-content *ngIf="expandedSections['statusHistory']">
          <div class="timeline">
            <div class="timeline-item" *ngFor="let event of shipment.statusHistory; let first = first; let last = last">
              <div class="timeline-marker" [ngClass]="{'timeline-marker-first': first, 'timeline-marker-last': last}">
                <mat-icon>{{ event.automated ? 'auto_mode' : 'person' }}</mat-icon>
              </div>
              <div class="timeline-content">
                <div class="timeline-header">
                  <span class="timeline-status">{{ formatter.getStatusLabel(event.status, shipment.category, shipment.webhookStatus) }}</span>
                  <span class="timeline-timestamp">{{ formatter.formatDate(event.timestamp) }}</span>
                </div>
                <div class="timeline-details">
                  <div *ngIf="event.operator">
                    <strong>Operator:</strong> {{ ("ORDER_DETAILS_MANAGER.VALUES.OPERATORS." + event.operator) | productionTranslate }}
                  </div>
                  <div *ngIf="event.location">
                    <strong>Location:</strong> {{ event.location.city }}, {{ event.location.country }}
                  </div>
                  <div *ngIf="event.source">
                    <strong>Source:</strong>
                    <mat-chip class="source-chip">{{ event.source }}</mat-chip>
                  </div>
                  <div *ngIf="event.notes" class="timeline-notes">
                    <strong>Notes:</strong> {{ event.notes }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- ========================================
           10. PHASE TIMELINE CARD
           ======================================== -->
      <mat-card class="detail-card" *ngIf="shipment.phase">
        <mat-card-header (click)="toggleSection('phaseTimeline')" class="clickable-header">
          <mat-card-title>
            <mat-icon>linear_scale</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.SECTIONS.PHASE_TIMELINE" | productionTranslate }}
          </mat-card-title>
          <button mat-icon-button class="expand-button">
            <mat-icon>{{ expandedSections['phaseTimeline'] ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </mat-card-header>

        <mat-card-content *ngIf="expandedSections['phaseTimeline']">
          <!-- Progress Bar -->
          <div class="progress-section">
            <div class="progress-label">
              <span>{{ "ORDER_DETAILS_MANAGER.LABELS.PROGRESS" | productionTranslate }}</span>
              <span class="progress-percentage">{{ shipment.phase?.progress }}%</span>
            </div>
            <mat-progress-bar
              mode="determinate"
              [value]="shipment.phase?.progress"
              class="progress-bar">
            </mat-progress-bar>
          </div>

          <!-- Phase Steps -->
          <div class="phase-steps" *ngIf="shipment.phase?.timeline && (shipment.phase?.timeline?.length || 0) > 0">
            <div class="phase-step" *ngFor="let step of shipment.phase?.timeline"
                 [ngClass]="{
                   'step-current': step.phase === getCurrentTimelineStep(),
                   'step-completed-item': step.completed
                 }">
              <div class="step-marker" [ngClass]="{
                'step-completed': step.completed,
                'step-active': step.phase === getCurrentTimelineStep() && !step.completed
              }">
                <mat-icon *ngIf="step.completed">check_circle</mat-icon>
                <mat-icon *ngIf="!step.completed && step.phase === getCurrentTimelineStep()" class="pulse-icon">play_circle</mat-icon>
                <mat-icon *ngIf="!step.completed && step.phase !== getCurrentTimelineStep()">radio_button_unchecked</mat-icon>
              </div>
              <div class="step-content">
                <!-- ✅ FIX: Utiliser le phase pour récupérer la traduction au lieu du label hardcodé -->
                <div class="step-label">
                  {{ "PARCEL_DETAILS.TIMELINE.STEPS." + (step.phase || 'unknown').toUpperCase() + ".TITLE" | productionTranslate }}
                  <span *ngIf="step.phase === getCurrentTimelineStep() && !step.completed" class="current-badge">
                    <mat-icon>trending_flat</mat-icon>
                    {{ "ORDER_DETAILS_MANAGER.LABELS.CURRENT_STEP" | productionTranslate }}
                  </span>
                </div>
                <div class="step-description">
                  {{ "PARCEL_DETAILS.TIMELINE.STEPS." + (step.phase || 'unknown').toUpperCase() + ".DESCRIPTION" | productionTranslate }}
                </div>
                <div class="step-dates">
                  <span *ngIf="step.completedAt" class="completed-date">
                    <mat-icon>done</mat-icon>
                    {{ "ORDER_DETAILS_MANAGER.LABELS.COMPLETED" | productionTranslate }}: {{ formatter.formatDate(step.completedAt) }}
                  </span>
                  <span *ngIf="!step.completedAt && step.estimatedAt" class="estimated-date">
                    <mat-icon>schedule</mat-icon>
                    {{ "ORDER_DETAILS_MANAGER.LABELS.ESTIMATED" | productionTranslate }}: {{ formatter.formatDate(step.estimatedAt) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Message si timeline vide -->
          <div class="empty-timeline-message" *ngIf="!shipment.phase?.timeline || (shipment.phase?.timeline?.length || 0) === 0">
            <mat-icon>info</mat-icon>
            <p>{{ "ORDER_DETAILS_MANAGER.LABELS.NO_TIMELINE_DATA" | productionTranslate }}</p>
            <p class="sub-message">{{ "ORDER_DETAILS_MANAGER.LABELS.TIMELINE_WILL_UPDATE" | productionTranslate }}</p>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- ========================================
           11. WEBHOOK STATUS CARD (if present)
           ======================================== -->
      <mat-card class="detail-card" *ngIf="formatter.hasWebhookStatus(shipment)">
        <mat-card-header (click)="toggleSection('webhookStatus')" class="clickable-header">
          <mat-card-title>
            <mat-icon>webhook</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.SECTIONS.WEBHOOK_STATUS" | productionTranslate }}
          </mat-card-title>
          <button mat-icon-button class="expand-button">
            <mat-icon>{{ expandedSections['webhookStatus'] ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </mat-card-header>

        <mat-card-content *ngIf="expandedSections['webhookStatus']">
          <div class="webhook-content">
            <!-- Overall Status -->
            <div class="detail-item">
              <label>{{ "ORDER_DETAILS_MANAGER.LABELS.OVERALL_STATUS" | productionTranslate }}</label>
              <mat-chip [ngClass]="'webhook-chip-' + formatter.getWebhookOverallStatus(shipment)" class="status-chip">
                {{ formatter.getWebhookOverallStatus(shipment) | uppercase }}
              </mat-chip>
            </div>

            <!-- Action Required -->
            <div class="detail-item" *ngIf="formatter.getWebhookActionRequired(shipment)">
              <mat-icon class="warning-icon">warning</mat-icon>
              <span class="warning-text">{{ "ORDER_DETAILS_MANAGER.LABELS.ACTION_REQUIRED" | productionTranslate }}</span>
            </div>

            <!-- Contact Support -->
            <div class="detail-item" *ngIf="formatter.getWebhookContactSupport(shipment)">
              <mat-icon class="error-icon">error</mat-icon>
              <span class="error-text">{{ "ORDER_DETAILS_MANAGER.LABELS.CONTACT_SUPPORT" | productionTranslate }}</span>
            </div>

            <!-- Stages Detail (show brief summary in collapsed view) -->
            <div class="webhook-stages">
              <h4>{{ "ORDER_DETAILS_MANAGER.LABELS.STAGES" | productionTranslate }}</h4>
              <!-- Payment Stage -->
              <div class="stage-item" *ngIf="formatter.hasWebhookPaymentStage(shipment)">
                <strong>Payment:</strong>
                <mat-chip class="stage-chip">{{ formatter.getWebhookPaymentStatus(shipment) }}</mat-chip>
              </div>
              <!-- DPD Stage -->
              <div class="stage-item" *ngIf="formatter.hasWebhookDpdStage(shipment)">
                <strong>DPD:</strong>
                <mat-chip class="stage-chip">{{ formatter.getWebhookDpdStatus(shipment) }}</mat-chip>
              </div>
              <!-- Collection Stage -->
              <div class="stage-item" *ngIf="formatter.hasWebhookCollectionStage(shipment)">
                <strong>Collection:</strong>
                <mat-chip class="stage-chip">{{ formatter.getWebhookCollectionStatus(shipment) }}</mat-chip>
              </div>
              <!-- Label Stage -->
              <div class="stage-item" *ngIf="formatter.hasWebhookLabelStage(shipment)">
                <strong>Label:</strong>
                <mat-chip class="stage-chip">{{ formatter.getWebhookLabelStatus(shipment) }}</mat-chip>
              </div>
              <!-- Notifications Stage -->
              <div class="stage-item" *ngIf="formatter.hasWebhookNotificationsStage(shipment)">
                <strong>Notifications:</strong>
                <mat-chip class="stage-chip">{{ formatter.getWebhookNotificationsEmail(shipment) }}</mat-chip>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- ========================================
           11b. NOTIFICATION LOGS CARD
           ======================================== -->
      <mat-card class="detail-card" *ngIf="isAdmin">
        <mat-card-header (click)="toggleSection('notificationLogs')" class="clickable-header">
          <mat-card-title>
            <mat-icon>notifications</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.NOTIFICATION_LOGS.TITLE" | productionTranslate }}
            <span class="notification-count-badge" *ngIf="notificationLogs.length > 0">({{ notificationLogs.length }})</span>
          </mat-card-title>
          <button mat-icon-button class="expand-button">
            <mat-icon>{{ expandedSections['notificationLogs'] ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </mat-card-header>

        <mat-card-content *ngIf="expandedSections['notificationLogs']">
          <!-- Empty state -->
          <div class="notification-empty" *ngIf="notificationLogs.length === 0">
            <mat-icon>notifications_off</mat-icon>
            <p>{{ "ORDER_DETAILS_MANAGER.NOTIFICATION_LOGS.EMPTY" | productionTranslate }}</p>
          </div>

          <!-- Notification feed -->
          <div class="notification-feed" *ngIf="notificationLogs.length > 0">
            <div class="notification-entry" *ngFor="let log of notificationLogs"
                 [ngClass]="{
                   'notification-entry-failed': log.status === 'failed' && !log.resolvedAt,
                   'notification-entry-resolved': log.resolvedAt
                 }">
              <div class="notification-entry-marker">
                <mat-icon *ngIf="log.type === 'sms'">sms</mat-icon>
                <mat-icon *ngIf="log.type === 'email'">email</mat-icon>
              </div>

              <div class="notification-entry-content">
                <div class="notification-entry-header">
                  <span class="notification-entry-date">{{ formatNotificationDate(log.createdAt) }}</span>
                  <span class="notification-entry-dash">&mdash;</span>
                  <span class="notification-entry-type-label">
                    {{ log.type === 'sms' ? ("ORDER_DETAILS_MANAGER.NOTIFICATION_LOGS.TYPE_SMS" | productionTranslate) : ("ORDER_DETAILS_MANAGER.NOTIFICATION_LOGS.TYPE_EMAIL" | productionTranslate) }}
                    "{{ getNotificationTypeLabel(log.notificationType) }}"
                  </span>
                </div>

                <!-- Subject (email) or message preview (sms) -->
                <div class="notification-entry-preview" *ngIf="log.subject || log.messagePreview">
                  <mat-icon class="inline-icon">{{ log.type === 'sms' ? 'chat' : 'subject' }}</mat-icon>
                  <span class="notification-preview-text">{{ log.subject || log.messagePreview }}</span>
                </div>

                <div class="notification-entry-details">
                  <span class="notification-entry-recipient">
                    <mat-icon class="inline-icon">{{ log.type === 'sms' ? 'phone' : 'alternate_email' }}</mat-icon>
                    {{ log.recipientMasked }}
                  </span>

                  <!-- Status badge -->
                  <mat-chip class="notification-badge-sent" *ngIf="log.status === 'sent'">
                    {{ "ORDER_DETAILS_MANAGER.NOTIFICATION_LOGS.SENT" | productionTranslate }}
                  </mat-chip>
                  <mat-chip class="notification-badge-failed" *ngIf="log.status === 'failed' && !log.resolvedAt">
                    {{ "ORDER_DETAILS_MANAGER.NOTIFICATION_LOGS.FAILED" | productionTranslate }}
                  </mat-chip>
                  <mat-chip class="notification-badge-resolved" *ngIf="log.resolvedAt">
                    {{ "ORDER_DETAILS_MANAGER.NOTIFICATION_LOGS.RESOLVED" | productionTranslate }}
                  </mat-chip>

                  <span class="notification-entry-attempts">
                    {{ "ORDER_DETAILS_MANAGER.NOTIFICATION_LOGS.ATTEMPTS" | productionTranslate }} {{ log.attempts }}/{{ log.maxRetries }}
                  </span>
                </div>

                <!-- Error details + retry button -->
                <div class="notification-error" *ngIf="log.status === 'failed' && !log.resolvedAt">
                  <div class="notification-error-message">
                    <mat-icon>warning</mat-icon>
                    <span *ngIf="log.errorCode">[{{ log.errorCode }}]</span>
                    {{ log.errorMessage }}
                  </div>
                  <button
                    mat-raised-button
                    class="retry-btn"
                    *ngIf="log.canRetry"
                    (click)="retryNotification(log.id)"
                    [disabled]="isRetrying[log.id]">
                    <mat-spinner diameter="16" *ngIf="isRetrying[log.id]"></mat-spinner>
                    <mat-icon *ngIf="!isRetrying[log.id]">refresh</mat-icon>
                    {{ "ORDER_DETAILS_MANAGER.NOTIFICATION_LOGS.RETRY" | productionTranslate }}
                  </button>
                </div>

                <!-- Resolved info -->
                <div class="notification-resolved-info" *ngIf="log.resolvedAt">
                  <mat-icon>check_circle</mat-icon>
                  {{ "ORDER_DETAILS_MANAGER.NOTIFICATION_LOGS.RESOLVED" | productionTranslate }}
                  {{ formatNotificationDate(log.resolvedAt) }}
                </div>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- ========================================
           12. RAW DATA CARD
           ======================================== -->
      <mat-card class="detail-card">
        <mat-card-header (click)="toggleSection('rawData')" class="clickable-header">
          <mat-card-title>
            <mat-icon>code</mat-icon>
            {{ "ORDER_DETAILS_MANAGER.SECTIONS.RAW_DATA" | productionTranslate }}
          </mat-card-title>
          <button mat-icon-button class="expand-button">
            <mat-icon>{{ expandedSections['rawData'] ? 'expand_less' : 'expand_more' }}</mat-icon>
          </button>
        </mat-card-header>

        <mat-card-content *ngIf="expandedSections['rawData']">
          <div class="raw-data-header">
            <button mat-raised-button (click)="copyToClipboard(JSON.stringify(shipment, null, 2), 'Raw Data')" class="befret-button-outline">
              <mat-icon>content_copy</mat-icon>
              {{ "COMMON.ACTIONS.COPY_ALL" | productionTranslate }}
            </button>
          </div>
          <pre class="json-viewer"><code>{{ shipment | json }}</code></pre>
        </mat-card-content>
      </mat-card>

      <!-- Save/Cancel Buttons (Edit Mode - Bottom) -->
      <div class="edit-mode-toolbar bottom-toolbar" *ngIf="isEditMode">
        <div class="toolbar-content">
          <mat-icon>info_outline</mat-icon>
          <span>{{ "ORDER_DETAILS_MANAGER.EDIT_MODE.REMINDER" | productionTranslate }}</span>
          <div class="toolbar-actions">
            <button mat-raised-button (click)="cancelEdit()" class="befret-button-outline" [disabled]="isSaving">
              <mat-icon>cancel</mat-icon>
              {{ "COMMON.ACTIONS.CANCEL" | productionTranslate }}
            </button>
            <button mat-raised-button (click)="saveChanges()" class="befret-button" [disabled]="isSaving || !editForm.valid">
              <mat-icon>save</mat-icon>
              {{ "COMMON.ACTIONS.SAVE" | productionTranslate }}
            </button>
          </div>
        </div>
      </div>

    </div>
  </section>

</main>
